import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SafewardClient, SafewardHttpError } from '../safeward-client.js'
import type {
  AppSettings,
  DecideResponse,
  HealthResponse,
  PendingTx,
} from '../types.js'

export type HealthState =
  | { kind: 'healthy'; pending: number }
  | { kind: 'offline'; message: string }
  | { kind: 'auth-required' }
  | { kind: 'unknown' }

export interface UsePendingState {
  health: HealthState
  guardianAddress: string | null
  txs: PendingTx[]
  decisions: Map<string, DecideResponse>
  decisionErrors: Map<string, string>
  lastRefreshed: Date | null
  moduleSafe: string
  refresh: () => Promise<void>
}

/**
 * Polls /v1/health + /v1/pending and runs /v1/decide for each tx that
 * doesn't have a cached verdict yet.
 *
 * Stable-identity callbacks: `client`, `simulateOne`, and `refresh` all stay
 * referentially stable across unrelated state changes. Without this, the old
 * version triggered a poll storm — `refresh` rebuilt every time a new
 * decision landed (because `decisions` was in its dep array), the
 * `useEffect(() => refresh(), [refresh])` re-fired, and the auto-refresh
 * setInterval reset on every dep churn. With N pending txs that produced
 * O(N) extra polls per refresh cycle. Now `refresh` reads the latest
 * `decisions` via a ref instead of a dep, so its identity only depends on
 * the (empty-dep) `simulateOne`.
 */
export function usePending(settings: AppSettings): UsePendingState {
  const [health, setHealth] = useState<HealthState>({ kind: 'unknown' })
  const [guardianAddress, setGuardian] = useState<string | null>(null)
  const [txs, setTxs] = useState<PendingTx[]>([])
  const [decisions, setDecisions] = useState<Map<string, DecideResponse>>(
    new Map(),
  )
  const [decisionErrors, setDecisionErrors] = useState<Map<string, string>>(
    new Map(),
  )
  const [lastRefreshed, setLast] = useState<Date | null>(null)
  const [moduleSafe, setModuleSafe] = useState(settings.moduleSafe)

  const inflight = useRef<Set<string>>(new Set())

  // Read-only refs for state we don't want in callback dep arrays. Updated
  // every render so callbacks always see the latest values.
  const decisionsRef = useRef(decisions)
  decisionsRef.current = decisions
  const moduleSafeFilterRef = useRef(settings.moduleSafe)
  moduleSafeFilterRef.current = settings.moduleSafe

  // Memoize the client so it's only re-created when the URL or token
  // actually changes — not on every state update.
  const client = useMemo(
    () =>
      new SafewardClient({
        baseUrl: settings.safewardUrl,
        authToken: settings.safewardAuthToken,
      }),
    [settings.safewardUrl, settings.safewardAuthToken],
  )
  const clientRef = useRef(client)
  clientRef.current = client

  const simulateOne = useCallback(async (tx: PendingTx) => {
    if (inflight.current.has(tx.safeTxHash)) return
    inflight.current.add(tx.safeTxHash)
    try {
      const res = await clientRef.current.decide(tx)
      setDecisions((prev) => new Map(prev).set(tx.safeTxHash, res))
      setDecisionErrors((prev) => {
        if (!prev.has(tx.safeTxHash)) return prev
        const next = new Map(prev)
        next.delete(tx.safeTxHash)
        return next
      })
    } catch (err) {
      setDecisionErrors((prev) =>
        new Map(prev).set(tx.safeTxHash, (err as Error).message),
      )
    } finally {
      inflight.current.delete(tx.safeTxHash)
    }
  }, [])

  const refresh = useCallback(async () => {
    const c = clientRef.current
    let healthRes: HealthResponse
    try {
      healthRes = await c.health()
      setGuardian(healthRes.guardianAddress.toLowerCase())
    } catch (err) {
      setHealth({ kind: 'offline', message: (err as Error).message })
      setTxs([])
      return
    }

    try {
      const pending = await c.pending(moduleSafeFilterRef.current || undefined)
      setModuleSafe(pending.moduleSafe)
      setTxs(pending.txs)
      setLast(new Date())
      setHealth({ kind: 'healthy', pending: pending.txs.length })
      setDecisions((prev) => {
        const live = new Set(pending.txs.map((t) => t.safeTxHash))
        const next = new Map<string, DecideResponse>()
        for (const [hash, res] of prev) if (live.has(hash)) next.set(hash, res)
        return next
      })
      // Read latest decisions via ref. Using the state value here would
      // either capture a stale closure (when refresh's deps don't include
      // it) or trigger refresh-rebuild + poll storms (when they do).
      for (const tx of pending.txs) {
        if (!decisionsRef.current.has(tx.safeTxHash)) void simulateOne(tx)
      }
    } catch (err) {
      // Branch on the structured status, not on substring("401") — Module
      // Safe addresses can contain "401" in their hex (it's the URL in the
      // error message) which used to trip the auth-required check.
      if (err instanceof SafewardHttpError && err.status === 401) {
        setHealth({ kind: 'auth-required' })
      } else if (err instanceof SafewardHttpError && err.status >= 500) {
        setHealth({
          kind: 'offline',
          message: `safeward backend ${err.status} — try again or check Worker logs`,
        })
      } else {
        setHealth({ kind: 'offline', message: (err as Error).message })
      }
    }
  }, [simulateOne])

  // Initial load + refetch when the client identity (URL/token) or the
  // moduleSafe filter changes. `refresh` is stable, so this effect doesn't
  // re-fire on unrelated state churn.
  useEffect(() => {
    void refresh()
  }, [refresh, client, settings.moduleSafe])

  // Auto-refresh on an interval. Pulls the latest `refresh` via a ref so
  // the timer isn't reset every time refresh's identity changes (which it
  // shouldn't, but the ref is cheap insurance).
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh
  useEffect(() => {
    // Belt-and-braces alongside storage.parseRefreshSeconds: NaN/<1
    // shouldn't reach here, but if it does, setInterval(NaN) clamps to
    // 4ms which would poll-storm the worker.
    const seconds = settings.autoRefreshSeconds
    if (!Number.isFinite(seconds) || seconds < 1) return
    const t = setInterval(() => void refreshRef.current(), seconds * 1000)
    return () => clearInterval(t)
  }, [settings.autoRefreshSeconds])

  return {
    health,
    guardianAddress,
    txs,
    decisions,
    decisionErrors,
    lastRefreshed,
    moduleSafe,
    refresh,
  }
}
